import { create } from 'zustand';
import type {
  ParsedAgenda,
  TranscriptSegment,
  SessionStatus,
  SessionSummary,
  SessionTiming,
  SessionReminder,
  ActionItem,
  CoachingAlert,
  CoachingSettings,
  RehearsalQuestion,
  RehearsalSettings,
} from '@/types';
import { parseAgendaText } from '@/services/agenda-parser';

// Default coaching settings
const DEFAULT_COACHING_SETTINGS: CoachingSettings = {
  talkRatioThreshold: 70,
  wpmThreshold: 170,
  defaultDurationMin: 30,
  enableHalfwayAlert: true,
  enableFiveMinAlert: true,
  enableActionCapture: true,
  includeProspectActions: true,
  actionTriggerPhrase: 'let me note that',
  maxCallTimeMin: 90, // Auto-end after 90 minutes by default (prevents forgotten recordings)
};

interface SessionState {
  status: SessionStatus;
  agenda: ParsedAgenda | null;
  agendaRawText: string;
  startedAt: number | null;
  endedAt: number | null;
  transcript: TranscriptSegment[];
  error: string | null;
  connectionStatus: 'disconnected' | 'connecting' | 'connected';

  // Database session tracking (set after POST /api/sessions)
  sessionId: string | null;

  // Feedback tracking
  llmExpansionUsed: boolean;
  sweepMatchCount: number;

  // Coaching state
  sessionTiming: SessionTiming | null;
  reminders: SessionReminder[];
  actionItems: ActionItem[];
  coachingAlerts: CoachingAlert[];
  coachingSettings: CoachingSettings;

  // Real-time coaching metrics (derived from speakerTalkTime)
  talkRatio: { userTalkTimeMs: number; otherTalkTimeMs: number };
  wpmSegments: { words: number; durationMs: number; timestamp: number }[];

  // Speaker identification for talk ratio
  speakerTalkTime: Record<number, number>; // Talk time per speaker ID (in ms)
  userSpeakerId: number | null; // Which speaker ID is "you" (null = auto-detect as dominant speaker)
  speakerLabels: Record<number, string>; // Custom labels for speakers (e.g., 0 -> "You", 1 -> "Customer")

  // Methodology selection
  selectedMethodologyId: string | null;

  // Rehearsal mode state
  isRehearsalMode: boolean;
  rehearsalSettings: RehearsalSettings | null;
  rehearsalSessionId: string | null;
  currentQuestion: RehearsalQuestion | null;
  questionHistory: RehearsalQuestion[];
  isGeneratingQuestion: boolean;
  isEvaluatingAnswer: boolean;

  // Phase 2: Timer and voice answer state
  questionTimerMs: number | null; // Timer duration (null = Easy mode, no timer)
  questionTimerStartedAt: number | null; // When the timer started
  isListeningForAnswer: boolean; // True when trigger phrase detected
  answerTranscriptBuffer: string; // Accumulates speech after trigger
  lastAnswerSpeechAt: number | null; // For silence detection
}

// Serializable state for cross-window sync
export interface SyncableSessionState {
  status: SessionStatus;
  agenda: ParsedAgenda | null;
  startedAt: number | null;
  endedAt: number | null;
  connectionStatus: 'disconnected' | 'connecting' | 'connected';
  sessionId: string | null;
  sessionTiming: SessionTiming | null;
  reminders: SessionReminder[];
  actionItems: ActionItem[];
  talkRatio: { userTalkTimeMs: number; otherTalkTimeMs: number };
  speakerTalkTime: Record<number, number>;
  userSpeakerId: number | null;
  speakerLabels: Record<number, string>;
  // Only include recent transcript segments for popup sync (last 10 for perf)
  recentTranscript: TranscriptSegment[];
}

interface SessionActions {
  setAgendaFromText: (text: string) => void;
  clearAgenda: () => void;
  startSession: () => void;
  endSession: () => void;
  resetSession: () => void;
  addTranscriptSegment: (segment: TranscriptSegment) => void;
  markItemCovered: (itemId: string, confidence: number, matchedText?: string) => void;
  markItemCoveredBySweep: (itemId: string, confidence: number, matchedText?: string) => void;
  setError: (error: string | null) => void;
  setConnectionStatus: (status: 'disconnected' | 'connecting' | 'connected') => void;
  setLlmExpansionUsed: (used: boolean) => void;
  setSessionId: (id: string | null) => void;
  getSummary: () => SessionSummary | null;
  getElapsedTime: () => number;

  // Cross-window sync
  getSyncableState: () => SyncableSessionState;
  syncFromBroadcast: (state: SyncableSessionState) => void;

  // Coaching actions
  setSessionTiming: (timing: SessionTiming | null) => void;
  addReminder: (text: string) => void;
  toggleReminder: (id: string) => void;
  deleteReminder: (id: string) => void;
  addActionItem: (item: ActionItem) => void;
  toggleActionItem: (id: string) => void;
  deleteActionItem: (id: string) => void;
  addCoachingAlert: (alert: Omit<CoachingAlert, 'id' | 'dismissed'>) => void;
  dismissCoachingAlert: (id: string) => void;
  updateCoachingSettings: (settings: Partial<CoachingSettings>) => void;
  updateTalkRatio: (userMs: number, otherMs: number) => void;
  addWPMSegment: (words: number, durationMs: number) => void;

  // Speaker tracking actions
  addSpeakerTalkTime: (speakerId: number, durationMs: number) => void;
  setUserSpeakerId: (speakerId: number | null) => void;
  swapUserSpeaker: () => void; // Swap user to the other detected speaker
  setSpeakerLabel: (speakerId: number, label: string) => void;
  getEffectiveUserSpeakerId: () => number | null; // Returns user speaker (or auto-detected dominant)

  // Methodology actions
  setSelectedMethodologyId: (id: string | null) => void;

  // Rehearsal mode actions
  enableRehearsalMode: (settings: RehearsalSettings) => void;
  disableRehearsalMode: () => void;
  setRehearsalSessionId: (id: string | null) => void;
  setCurrentQuestion: (question: RehearsalQuestion | null) => void;
  addToQuestionHistory: (question: RehearsalQuestion) => void;
  updateQuestionInHistory: (id: string, updates: Partial<RehearsalQuestion>) => void;
  setGeneratingQuestion: (loading: boolean) => void;
  setEvaluatingAnswer: (loading: boolean) => void;

  // Phase 2: Timer and voice answer actions
  startQuestionTimer: (durationMs: number) => void;
  stopQuestionTimer: () => void;
  getQuestionTimeRemaining: () => number | null;
  startAnswerListening: () => void;
  stopAnswerListening: () => void;
  appendToAnswerBuffer: (text: string) => void;
  clearAnswerBuffer: () => void;
  getAnswerBuffer: () => string;
}

type SessionStore = SessionState & SessionActions;

export const useSessionStore = create<SessionStore>((set, get) => ({
  // Initial state
  status: 'idle',
  agenda: null,
  agendaRawText: '',
  startedAt: null,
  endedAt: null,
  transcript: [],
  error: null,
  connectionStatus: 'disconnected',
  sessionId: null,
  llmExpansionUsed: false,
  sweepMatchCount: 0,

  // Coaching initial state
  sessionTiming: null,
  reminders: [],
  actionItems: [],
  coachingAlerts: [],
  coachingSettings: DEFAULT_COACHING_SETTINGS,
  talkRatio: { userTalkTimeMs: 0, otherTalkTimeMs: 0 },
  wpmSegments: [],

  // Speaker tracking initial state
  speakerTalkTime: {},
  userSpeakerId: null,
  speakerLabels: {},

  // Methodology initial state
  selectedMethodologyId: null,

  // Rehearsal mode initial state
  isRehearsalMode: false,
  rehearsalSettings: null,
  rehearsalSessionId: null,
  currentQuestion: null,
  questionHistory: [],
  isGeneratingQuestion: false,
  isEvaluatingAnswer: false,

  // Phase 2: Timer and voice answer initial state
  questionTimerMs: null,
  questionTimerStartedAt: null,
  isListeningForAnswer: false,
  answerTranscriptBuffer: '',
  lastAnswerSpeechAt: null,

  setAgendaFromText: (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      set({ agenda: null, agendaRawText: '' });
      return;
    }
    set({ agenda: parseAgendaText(trimmed), agendaRawText: text, error: null });
  },

  clearAgenda: () => {
    set({
      agenda: null,
      agendaRawText: '',
      status: 'idle',
      startedAt: null,
      endedAt: null,
      transcript: [],
      error: null,
    });
  },

  startSession: () => {
    const { agenda, coachingSettings, sessionTiming } = get();
    if (!agenda || agenda.items.length === 0) {
      set({ error: 'Please add some talking points first' });
      return;
    }

    const resetItems = agenda.items.map(item => ({
      ...item,
      covered: item.isGroupHeader ? true : false, // Headers are auto-covered (not tracked)
      coveredAt: null,
      confidence: 0,
    }));

    // Set default timing if not configured
    const timing = sessionTiming || {
      scheduledDurationMin: coachingSettings.defaultDurationMin,
      alerts: {
        halfway: coachingSettings.enableHalfwayAlert,
        fiveMinWarning: coachingSettings.enableFiveMinAlert,
      },
    };

    set({
      status: 'listening',
      startedAt: Date.now(),
      endedAt: null,
      transcript: [],
      error: null,
      agenda: { ...agenda, items: resetItems },
      llmExpansionUsed: false,
      sweepMatchCount: 0,
      // Reset coaching state for new session but keep reminders
      sessionTiming: timing,
      actionItems: [],
      coachingAlerts: [],
      talkRatio: { userTalkTimeMs: 0, otherTalkTimeMs: 0 },
      wpmSegments: [],
      // Reset speaker tracking
      speakerTalkTime: {},
      userSpeakerId: null,
      speakerLabels: { 0: 'You', 1: 'Other' }, // Default labels
    });
  },

  endSession: () => {
    set({ status: 'complete', endedAt: Date.now() });
  },

  resetSession: () => {
    set({
      status: 'idle',
      startedAt: null,
      endedAt: null,
      transcript: [],
      error: null,
      agenda: null,
      agendaRawText: '',
      connectionStatus: 'disconnected',
      sessionId: null,
      llmExpansionUsed: false,
      sweepMatchCount: 0,
      // Reset coaching state
      sessionTiming: null,
      reminders: [],
      actionItems: [],
      coachingAlerts: [],
      talkRatio: { userTalkTimeMs: 0, otherTalkTimeMs: 0 },
      wpmSegments: [],
      // Reset speaker tracking
      speakerTalkTime: {},
      userSpeakerId: null,
      speakerLabels: {},
      // Reset methodology
      selectedMethodologyId: null,
      // Reset rehearsal mode
      isRehearsalMode: false,
      rehearsalSettings: null,
      rehearsalSessionId: null,
      currentQuestion: null,
      questionHistory: [],
      isGeneratingQuestion: false,
      isEvaluatingAnswer: false,
      // Reset Phase 2 timer and voice state
      questionTimerMs: null,
      questionTimerStartedAt: null,
      isListeningForAnswer: false,
      answerTranscriptBuffer: '',
      lastAnswerSpeechAt: null,
    });
  },

  addTranscriptSegment: (segment: TranscriptSegment) => {
    set(state => ({
      transcript: [...state.transcript, segment],
    }));
  },

  markItemCovered: (itemId: string, confidence: number, matchedText?: string) => {
    set(state => {
      if (!state.agenda) return state;

      const updatedItems = state.agenda.items.map(item => {
        if (item.id === itemId && !item.covered) {
          return { ...item, covered: true, coveredAt: Date.now(), confidence, matchedText };
        }
        return item;
      });

      return { agenda: { ...state.agenda, items: updatedItems } };
    });
  },

  // Same as markItemCovered but also increments sweepMatchCount
  markItemCoveredBySweep: (itemId: string, confidence: number, matchedText?: string) => {
    set(state => {
      if (!state.agenda) return state;

      const item = state.agenda.items.find(i => i.id === itemId);
      if (!item || item.covered) return state; // Don't count if already covered

      const updatedItems = state.agenda.items.map(i => {
        if (i.id === itemId && !i.covered) {
          return { ...i, covered: true, coveredAt: Date.now(), confidence, matchedText };
        }
        return i;
      });

      return {
        agenda: { ...state.agenda, items: updatedItems },
        sweepMatchCount: state.sweepMatchCount + 1,
      };
    });
  },

  setError: (error: string | null) => set({ error }),

  setLlmExpansionUsed: (used: boolean) => set({ llmExpansionUsed: used }),

  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),

  setSessionId: (sessionId: string | null) => set({ sessionId }),

  // Cross-window sync
  getSyncableState: (): SyncableSessionState => {
    const state = get();
    // Only include last 10 final transcript segments to avoid large payloads
    const recentTranscript = state.transcript
      .filter(s => s.isFinal)
      .slice(-10);
    return {
      status: state.status,
      agenda: state.agenda,
      startedAt: state.startedAt,
      endedAt: state.endedAt,
      connectionStatus: state.connectionStatus,
      sessionId: state.sessionId,
      sessionTiming: state.sessionTiming,
      reminders: state.reminders,
      actionItems: state.actionItems,
      talkRatio: state.talkRatio,
      speakerTalkTime: state.speakerTalkTime,
      userSpeakerId: state.userSpeakerId,
      speakerLabels: state.speakerLabels,
      recentTranscript,
    };
  },

  syncFromBroadcast: (syncState: SyncableSessionState) => {
    set({
      status: syncState.status,
      agenda: syncState.agenda,
      startedAt: syncState.startedAt,
      endedAt: syncState.endedAt,
      connectionStatus: syncState.connectionStatus,
      sessionId: syncState.sessionId,
      sessionTiming: syncState.sessionTiming,
      reminders: syncState.reminders,
      actionItems: syncState.actionItems,
      talkRatio: syncState.talkRatio,
      speakerTalkTime: syncState.speakerTalkTime,
      userSpeakerId: syncState.userSpeakerId,
      speakerLabels: syncState.speakerLabels,
      // Replace transcript with recent segments from main window
      transcript: syncState.recentTranscript || [],
    });
  },

  // Coaching actions
  setSessionTiming: (timing) => set({ sessionTiming: timing }),

  addReminder: (text) => {
    set(state => ({
      reminders: [
        ...state.reminders,
        {
          id: crypto.randomUUID(),
          text,
          completed: false,
        },
      ],
    }));
  },

  toggleReminder: (id) => {
    set(state => ({
      reminders: state.reminders.map(r =>
        r.id === id
          ? { ...r, completed: !r.completed, completedAt: !r.completed ? Date.now() : undefined }
          : r
      ),
    }));
  },

  deleteReminder: (id) => {
    set(state => ({
      reminders: state.reminders.filter(r => r.id !== id),
    }));
  },

  addActionItem: (item) => {
    set(state => ({
      actionItems: [...state.actionItems, item],
    }));
  },

  toggleActionItem: (id) => {
    set(state => ({
      actionItems: state.actionItems.map(item =>
        item.id === id ? { ...item, completed: !item.completed } : item
      ),
    }));
  },

  deleteActionItem: (id) => {
    set(state => ({
      actionItems: state.actionItems.filter(item => item.id !== id),
    }));
  },

  addCoachingAlert: (alert) => {
    set(state => ({
      coachingAlerts: [
        ...state.coachingAlerts,
        {
          ...alert,
          id: crypto.randomUUID(),
          dismissed: false,
        },
      ],
    }));
  },

  dismissCoachingAlert: (id) => {
    set(state => ({
      coachingAlerts: state.coachingAlerts.map(alert =>
        alert.id === id ? { ...alert, dismissed: true } : alert
      ),
    }));
  },

  updateCoachingSettings: (settings) => {
    set(state => ({
      coachingSettings: { ...state.coachingSettings, ...settings },
    }));
  },

  updateTalkRatio: (userMs, otherMs) => {
    set(state => ({
      talkRatio: {
        userTalkTimeMs: state.talkRatio.userTalkTimeMs + userMs,
        otherTalkTimeMs: state.talkRatio.otherTalkTimeMs + otherMs,
      },
    }));
  },

  addWPMSegment: (words, durationMs) => {
    const now = Date.now();
    const windowMs = 30000; // 30 second window
    set(state => ({
      wpmSegments: [
        ...state.wpmSegments.filter(s => s.timestamp > now - windowMs),
        { words, durationMs, timestamp: now },
      ],
    }));
  },

  // Speaker tracking actions
  addSpeakerTalkTime: (speakerId: number, durationMs: number) => {
    set(state => {
      const newSpeakerTalkTime = { ...state.speakerTalkTime };
      newSpeakerTalkTime[speakerId] = (newSpeakerTalkTime[speakerId] || 0) + durationMs;

      // Auto-assign labels for new speakers
      const newSpeakerLabels = { ...state.speakerLabels };
      if (!(speakerId in newSpeakerLabels)) {
        const existingCount = Object.keys(newSpeakerLabels).length;
        newSpeakerLabels[speakerId] = existingCount === 0 ? 'You' : `Speaker ${existingCount + 1}`;
      }

      // Derive talkRatio from speakerTalkTime
      // If userSpeakerId is set, use that; otherwise, assume speaker 0 is user (auto-detect mode)
      const effectiveUserId = state.userSpeakerId ?? 0;
      let userTalkTimeMs = 0;
      let otherTalkTimeMs = 0;

      for (const [id, time] of Object.entries(newSpeakerTalkTime)) {
        if (Number(id) === effectiveUserId) {
          userTalkTimeMs = time;
        } else {
          otherTalkTimeMs += time;
        }
      }

      return {
        speakerTalkTime: newSpeakerTalkTime,
        speakerLabels: newSpeakerLabels,
        talkRatio: { userTalkTimeMs, otherTalkTimeMs },
      };
    });
  },

  setUserSpeakerId: (speakerId: number | null) => {
    set(state => {
      // Recalculate talkRatio based on new user speaker ID
      const effectiveUserId = speakerId ?? 0;
      let userTalkTimeMs = 0;
      let otherTalkTimeMs = 0;

      for (const [id, time] of Object.entries(state.speakerTalkTime)) {
        if (Number(id) === effectiveUserId) {
          userTalkTimeMs = time;
        } else {
          otherTalkTimeMs += time;
        }
      }

      // Update labels to reflect the swap
      const newLabels = { ...state.speakerLabels };
      // Set the new user speaker label to "You"
      if (speakerId !== null) {
        // Find current "You" speaker and relabel
        for (const [id, label] of Object.entries(state.speakerLabels)) {
          if (label === 'You' && Number(id) !== speakerId) {
            newLabels[Number(id)] = 'Other';
          }
        }
        newLabels[speakerId] = 'You';
      }

      return {
        userSpeakerId: speakerId,
        speakerLabels: newLabels,
        talkRatio: { userTalkTimeMs, otherTalkTimeMs },
      };
    });
  },

  swapUserSpeaker: () => {
    set(state => {
      // Find all speaker IDs
      const speakerIds = Object.keys(state.speakerTalkTime).map(Number);
      if (speakerIds.length < 2) return state; // Can't swap with only one speaker

      // Get current effective user ID
      const currentUserId = state.userSpeakerId ?? 0;

      // Find the other speaker (the one with most talk time that isn't current user)
      const otherSpeakers = speakerIds.filter(id => id !== currentUserId);
      const newUserId = otherSpeakers[0]; // Just take the first other speaker

      // Recalculate talkRatio
      let userTalkTimeMs = 0;
      let otherTalkTimeMs = 0;

      for (const [id, time] of Object.entries(state.speakerTalkTime)) {
        if (Number(id) === newUserId) {
          userTalkTimeMs = time;
        } else {
          otherTalkTimeMs += time;
        }
      }

      // Swap labels
      const newLabels = { ...state.speakerLabels };
      newLabels[currentUserId] = 'Other';
      newLabels[newUserId] = 'You';

      return {
        userSpeakerId: newUserId,
        speakerLabels: newLabels,
        talkRatio: { userTalkTimeMs, otherTalkTimeMs },
      };
    });
  },

  setSpeakerLabel: (speakerId: number, label: string) => {
    set(state => ({
      speakerLabels: { ...state.speakerLabels, [speakerId]: label },
    }));
  },

  getEffectiveUserSpeakerId: (): number | null => {
    const state = get();
    // If explicitly set, return that
    if (state.userSpeakerId !== null) return state.userSpeakerId;

    // Auto-detect: assume speaker 0 is user (Deepgram's default for first speaker)
    // Or find the dominant speaker if we have data
    const speakerIds = Object.keys(state.speakerTalkTime).map(Number);
    if (speakerIds.length === 0) return null;

    // For auto-detect mode, just return 0 (first speaker detected is usually the user)
    return 0;
  },

  getSummary: (): SessionSummary | null => {
    const { agenda, startedAt, endedAt, status, transcript } = get();
    if (!agenda || status === 'idle') return null;

    const duration = startedAt ? (endedAt || Date.now()) - startedAt : 0;
    // Filter out group headers from coverage stats
    const trackableItems = agenda.items.filter(item => !item.isGroupHeader);
    const coveredItemsList = trackableItems.filter(item => item.covered);
    const missedItems = trackableItems.filter(item => !item.covered);

    // Build full transcript text from final segments only
    const fullTranscriptText = transcript
      .filter(s => s.isFinal)
      .map(s => s.text)
      .join(' ');

    return {
      duration,
      totalItems: trackableItems.length,
      coveredItems: coveredItemsList.length,
      missedItems,
      coveredItemsList,
      transcript,
      fullTranscriptText,
      startedAt: startedAt || 0,
    };
  },

  getElapsedTime: (): number => {
    const { startedAt, endedAt, status } = get();
    if (!startedAt) return 0;
    const end = status === 'complete' && endedAt ? endedAt : Date.now();
    return end - startedAt;
  },

  // Methodology actions
  setSelectedMethodologyId: (id: string | null) => set({ selectedMethodologyId: id }),

  // Rehearsal mode actions
  enableRehearsalMode: (settings: RehearsalSettings) => {
    set({
      isRehearsalMode: true,
      rehearsalSettings: settings,
      currentQuestion: null,
      questionHistory: [],
      isGeneratingQuestion: false,
      isEvaluatingAnswer: false,
    });
  },

  disableRehearsalMode: () => {
    set({
      isRehearsalMode: false,
      rehearsalSettings: null,
      rehearsalSessionId: null,
      currentQuestion: null,
      questionHistory: [],
      isGeneratingQuestion: false,
      isEvaluatingAnswer: false,
    });
  },

  setRehearsalSessionId: (id: string | null) => set({ rehearsalSessionId: id }),

  setCurrentQuestion: (question: RehearsalQuestion | null) => set({ currentQuestion: question }),

  addToQuestionHistory: (question: RehearsalQuestion) => {
    set(state => ({
      questionHistory: [...state.questionHistory, question],
    }));
  },

  updateQuestionInHistory: (id: string, updates: Partial<RehearsalQuestion>) => {
    set(state => ({
      questionHistory: state.questionHistory.map(q =>
        q.id === id ? { ...q, ...updates } : q
      ),
      currentQuestion: state.currentQuestion?.id === id
        ? { ...state.currentQuestion, ...updates }
        : state.currentQuestion,
    }));
  },

  setGeneratingQuestion: (loading: boolean) => set({ isGeneratingQuestion: loading }),

  setEvaluatingAnswer: (loading: boolean) => set({ isEvaluatingAnswer: loading }),

  // Phase 2: Timer and voice answer actions
  startQuestionTimer: (durationMs: number) => {
    set({
      questionTimerMs: durationMs,
      questionTimerStartedAt: Date.now(),
    });
  },

  stopQuestionTimer: () => {
    set({
      questionTimerMs: null,
      questionTimerStartedAt: null,
    });
  },

  getQuestionTimeRemaining: (): number | null => {
    const { questionTimerMs, questionTimerStartedAt } = get();
    if (questionTimerMs === null || questionTimerStartedAt === null) {
      return null;
    }
    const elapsed = Date.now() - questionTimerStartedAt;
    const remaining = questionTimerMs - elapsed;
    return Math.max(0, remaining);
  },

  startAnswerListening: () => {
    set({
      isListeningForAnswer: true,
      answerTranscriptBuffer: '',
      lastAnswerSpeechAt: Date.now(),
    });
  },

  stopAnswerListening: () => {
    set({
      isListeningForAnswer: false,
      lastAnswerSpeechAt: null,
    });
  },

  appendToAnswerBuffer: (text: string) => {
    set(state => ({
      answerTranscriptBuffer: state.answerTranscriptBuffer
        ? `${state.answerTranscriptBuffer} ${text}`
        : text,
      lastAnswerSpeechAt: Date.now(),
    }));
  },

  clearAnswerBuffer: () => {
    set({ answerTranscriptBuffer: '' });
  },

  getAnswerBuffer: (): string => {
    return get().answerTranscriptBuffer;
  },
}));

export const useAgenda = () => useSessionStore(state => state.agenda);
export const useAgendaRawText = () => useSessionStore(state => state.agendaRawText);
export const useSessionStatus = () => useSessionStore(state => state.status);
export const useSessionError = () => useSessionStore(state => state.error);

// Coaching selectors
export const useReminders = () => useSessionStore(state => state.reminders);
export const useActionItems = () => useSessionStore(state => state.actionItems);
export const useCoachingAlerts = () => useSessionStore(state => state.coachingAlerts);
export const useCoachingSettings = () => useSessionStore(state => state.coachingSettings);
export const useSessionTiming = () => useSessionStore(state => state.sessionTiming);
export const useTalkRatio = () => useSessionStore(state => state.talkRatio);
export const useWPMSegments = () => useSessionStore(state => state.wpmSegments);

// Speaker tracking selectors
export const useSpeakerTalkTime = () => useSessionStore(state => state.speakerTalkTime);
export const useUserSpeakerId = () => useSessionStore(state => state.userSpeakerId);
export const useSpeakerLabels = () => useSessionStore(state => state.speakerLabels);

// Methodology selectors
export const useSelectedMethodologyId = () => useSessionStore(state => state.selectedMethodologyId);

// Rehearsal mode selectors
export const useIsRehearsalMode = () => useSessionStore(state => state.isRehearsalMode);
export const useRehearsalSettings = () => useSessionStore(state => state.rehearsalSettings);
export const useRehearsalSessionId = () => useSessionStore(state => state.rehearsalSessionId);
export const useCurrentQuestion = () => useSessionStore(state => state.currentQuestion);
export const useQuestionHistory = () => useSessionStore(state => state.questionHistory);
export const useIsGeneratingQuestion = () => useSessionStore(state => state.isGeneratingQuestion);
export const useIsEvaluatingAnswer = () => useSessionStore(state => state.isEvaluatingAnswer);

// Phase 2: Timer and voice answer selectors
export const useQuestionTimerMs = () => useSessionStore(state => state.questionTimerMs);
export const useQuestionTimerStartedAt = () => useSessionStore(state => state.questionTimerStartedAt);
export const useIsListeningForAnswer = () => useSessionStore(state => state.isListeningForAnswer);
export const useAnswerTranscriptBuffer = () => useSessionStore(state => state.answerTranscriptBuffer);
export const useLastAnswerSpeechAt = () => useSessionStore(state => state.lastAnswerSpeechAt);
